import { Component, OnInit, Provider } from '@angular/core';
import { SearchObject } from '../../services/Rest';
import { BaseComponent } from '../base/base.component';
import { Currency_Rate, Item, Purchase, Store, User } from '../../models/RestModels';
import {PurchaseDetailInfo, PurchaseInfo, ShippingItemInfo, ShippingInfo} from 'src/app/models/models';
import {forkJoin, of} from 'rxjs';
import {Utils} from 'src/app/classes/Utils';
import {mergeMap} from 'rxjs/operators';
import { GetEmpty } from 'src/app/classes/Empties';


interface CPurchaseInfo extends PurchaseInfo
{
	store:Store;
}

interface BatchEntryRow
{
	batch: string;
	expiration_date: string;
	qty: number;
}

interface PurchaseDetailBatchEntry
{
	purchase_detail_id: number;
	item: Item;
	total_qty: number;
	batch_option: 'BATCH_ONLY' | 'EXPIRATION_ONLY' | 'BATCH_AND_EXPIRATION';
	rows: BatchEntryRow[];
}

@Component({
	selector: 'app-list-purchase',
	templateUrl: './list-purchase.component.html',
	styleUrls: ['./list-purchase.component.css']
})

export class ListPurchaseComponent extends BaseComponent implements OnInit
{
	file:File = null;
	show_import:boolean = false;
	purchase_search:SearchObject<Purchase> = this.getEmptySearch();
	purchase_info_list:CPurchaseInfo[] = [];
	currency_rate_list:Currency_Rate[] = [];
	provider_list:User[] = [];
	store_list: Store[];

	shipping_guide:string = '';
	shipping_date:string = '';
	shipping_company:string = '';
	search_start:string = '';
	search_end:string = '';
	selected_purchase_info: PurchaseInfo | null;
	show_create_shipping:boolean = false;
	mark_as_sent:boolean = false;

	// Batch entry modal
	show_batch_entry:boolean = false;
	batch_entries:PurchaseDetailBatchEntry[] = [];
	batch_purchase_info:PurchaseInfo | null = null;

	ngOnInit()
	{
		this.path = '/list-purchase';

		this.subs.sink = this.route.queryParamMap
		.pipe
		(
			mergeMap((queryParamMap)=>
			{
				this.is_loading = true;
				let fields = [ "provider_user_id","created","store_id" ];
				let extra_keys = ["paid_status"];

				this.purchase_search = this.getSearch(queryParamMap, fields, extra_keys );
				this.purchase_search.sort_order = ['id_DESC'];
				this.purchase_search.eq.status = 'ACTIVE';

				let start = new Date();
				let end = new Date();

				if(!queryParamMap.has('ge.created'))
				{
					start.setDate(1);
					start.setHours(0,0,0,0);
					this.purchase_search.ge.created = start;
				}
				this.search_start = Utils.getLocalMysqlStringFromDate(this.purchase_search.ge.created);

				if(!queryParamMap.has('le.created'))
				{
					let end_date = Utils.getEndOfMonth(end);
					end_date.setHours(23,59,0);
					this.purchase_search.le.created = end_date;
				}

				this.search_end = Utils.getLocalMysqlStringFromDate(this.purchase_search.le.created);
				this.setTitle('Ordenes de compra');
				this.current_page = this.purchase_search.page;

				return forkJoin
				({
					purchase_info: this.rest.purchase_info.search( this.purchase_search ),
					store: this.rest.getStores(true),
					currency_rate: this.rest.getCurrencyRates(),
					providers: this.rest.user.search({eq:{type: 'USER'}, search_extra:{'user_permission.is_provider':1},limit:99999})
				})

			})
		)
		.subscribe
		({
			error:(error)=>this.showError(error),
			next:(response)=>
			{
				this.setPages( this.purchase_search.page, response.purchase_info.total );
				this.store_list = response.store.data;
				response.providers.data.sort((a,b)=>{
					return a.name > b.name ? 1: -1;
				});
				this.provider_list = response.providers.data;

				for( let purchase_info of response.purchase_info.data )
				{
					purchase_info.purchase.total = this.getPurchaseInfoTotal(purchase_info);
				}

				this.purchase_info_list = response.purchase_info.data.map((pi:PurchaseInfo)=>
				{
					let store = this.store_list.find((s:Store)=>s.id == pi.purchase.store_id) || GetEmpty.store();
					return { ...pi, store };
				});

				this.currency_rate_list = response.currency_rate.data;
				this.is_loading = false;
			},
		});
	}

	addToStock(purchase_info:PurchaseInfo)
	{
		// Check if any item requires batch details
		let items_with_batch = purchase_info.details.filter(d => d.item.batch_option !== 'NONE' );

		if (items_with_batch.length > 0)
		{
			this.showBatchEntryModal(purchase_info, items_with_batch);
		}
		else
		{
			this.confirmAndAddToStock(purchase_info);
		}
	}

	confirmAndAddToStock(purchase_info:PurchaseInfo, batch_details?: any[])
	{
		this.subs.sink = this.confirmation.showConfirmAlert
		(
			purchase_info,
			'Agregar Inventario',
			'La orden de compra se agregara al inventario actual de la sucursal '+purchase_info.store.name+', esta seguro que desea continuar',
		)
		.subscribe((response)=>
		{
			if( response.accepted )
			{
				let payload = {
					purchase_id: purchase_info.purchase.id,
					batch_details: batch_details
				};

				this.subs.sink = this.rest.updatePath('addPurchaseToStock', payload ).subscribe((response)=>
				{
					this.showSuccess('El inventario se agrego con exito');
					purchase_info.purchase.stock_status = 'ADDED_TO_STOCK';
				},(error)=>{
					this.showError(error)
					this.show_batch_entry = true;
				});
			}
		})
	}

	showBatchEntryModal(purchase_info:PurchaseInfo, items_with_batch:PurchaseDetailInfo[])
	{
		this.batch_purchase_info = purchase_info;
		this.batch_entries = items_with_batch.map(d => ({
			purchase_detail_id: d.purchase_detail.id,
			item: d.item,
			total_qty: d.purchase_detail.qty,
			batch_option: d.item.batch_option as 'BATCH_ONLY' | 'EXPIRATION_ONLY' | 'BATCH_AND_EXPIRATION',
			rows: [{ batch: '', expiration_date: '', qty: d.purchase_detail.qty }]
		}));
		this.show_batch_entry = true;
	}

	addBatchRow(entry:PurchaseDetailBatchEntry)
	{
		entry.rows.push({ batch: '', expiration_date: '', qty: 0 });
	}

	removeBatchRow(entry:PurchaseDetailBatchEntry, index:number)
	{
		if (entry.rows.length > 1)
		{
			entry.rows.splice(index, 1);
		}
	}

	getTotalBatchQty(entry:PurchaseDetailBatchEntry):number
	{
		return entry.rows.reduce((sum, row) => sum + (row.qty || 0), 0);
	}

	validateBatchEntries():boolean
	{
		for (let entry of this.batch_entries)
		{
			let total = this.getTotalBatchQty(entry);
			if (total !== entry.total_qty)
			{
				this.showError(`La cantidad total de lotes para "${entry.item.name}" debe ser ${entry.total_qty}, actualmente es ${total}`);
				return false;
			}

			for (let row of entry.rows)
			{
				if (row.qty <= 0)
				{
					this.showError(`La cantidad debe ser mayor a 0 para "${entry.item.name}"`);
					return false;
				}

				if ((entry.batch_option === 'BATCH_ONLY' || entry.batch_option === 'BATCH_AND_EXPIRATION') && !row.batch)
				{
					this.showError(`El código de lote es requerido para "${entry.item.name}"`);
					return false;
				}

				if ((entry.batch_option === 'EXPIRATION_ONLY' || entry.batch_option === 'BATCH_AND_EXPIRATION') && !row.expiration_date)
				{
					this.showError(`La fecha de caducidad es requerida para "${entry.item.name}"`);
					return false;
				}
			}
		}
		return true;
	}

	submitBatchEntry()
	{
		if (!this.validateBatchEntries())
		{
			return;
		}

		let batch_details = this.batch_entries.map(entry => ({
			purchase_detail_id: entry.purchase_detail_id,
			quantities: entry.rows.map(row => {
				let q: any = { qty: row.qty };
				if (entry.batch_option === 'BATCH_ONLY' || entry.batch_option === 'BATCH_AND_EXPIRATION')
				{
					q.batch = row.batch;
				}
				if (entry.batch_option === 'EXPIRATION_ONLY' || entry.batch_option === 'BATCH_AND_EXPIRATION')
				{
					q.expiration_date = row.expiration_date;
				}
				return q;
			})
		}));

		this.show_batch_entry = false;
		this.confirmAndAddToStock(this.batch_purchase_info, batch_details);
	}

	cancelBatchEntry()
	{
		this.show_batch_entry = false;
		this.batch_entries = [];
		this.batch_purchase_info = null;
	}

	markAsSentChange(evt:Event)
	{
		let input = evt.target as HTMLInputElement;
		this.mark_as_sent = input.checked;
	}

	showCreateShipping( purchase_info:PurchaseInfo )
	{
		this.selected_purchase_info = purchase_info;
		this.show_create_shipping = true;
	}

	createShipping(evt:Event)
	{
		this.subs.sink = this.rest.purchase_info.get( this.selected_purchase_info.purchase.id )
		.pipe
		(
			mergeMap((pi:PurchaseInfo)=>
			{
				let shipping_info:ShippingInfo = {
					shipping: {
						to_store_id: pi.purchase.store_id,
						from_store_id: null,
						shipping_guide:this.shipping_guide,
						shipping_company:this.shipping_company,
						date: this.shipping_date,
						purchase_id: pi.purchase.id
					},
					items: []
				};

				pi.details.forEach((pdi:PurchaseDetailInfo)=>
				{
					let si:ShippingItemInfo = {
						shipping_item:
						{
							item_id: pdi.purchase_detail.item_id,
							qty: pdi.purchase_detail.qty,
							serial_number: pdi.purchase_detail.serial_number,
							received_qty: 0
						},
						shipping_item_batches: []
					};

					shipping_info.items.push(si);
				});

				return this.rest.shipping_info.create( shipping_info );
			})
		)
		.subscribe((response)=>
		{
			this.selected_purchase_info.purchase.stock_status = 'SHIPPING_CREATED';
			this.showSuccess('El envio se registro con exito')
			if( this.mark_as_sent )
			{
				this.subs.sink = this.rest.update
				(
					'markShippingAsSent',
					{shipping_id:response.shipping.id}
				)
				.subscribe(()=>
				{
					this.showSuccess('El envio se marco como enviado');
					this.show_create_shipping = false;
				},(error)=>
				{
					this.showSuccess('El envio se registro con exito pero no se pudo marcar como enviado');
					this.showError(error);
					this.mark_as_sent = false;
					this.show_create_shipping = false;
				})
			}
			else
			{
				this.show_create_shipping = false;
				this.mark_as_sent = false;
				this.show_create_shipping = false;

				let form = evt.target as HTMLFormElement;
				form.reset();

			}
		},(error)=>this.showError(error));
	}

	getPurchaseInfoTotal(purchase_info:PurchaseInfo):number
	{
		let total = 0;

		for( let detail of purchase_info.details )
		{
			total += (detail.purchase_detail.unitary_price*detail.purchase_detail.qty);
		}

		return total;
	}

	deletePurchase(purchase_info: CPurchaseInfo)
	{
		this.subs.sink = this.confirmation.showConfirmAlert
		(
			purchase_info,
			'Eliminar Compra',
			'La orden de compra se eliminara, esta seguro que desea continuar',
		)
		.pipe
		(
			mergeMap((response) =>
			{
				if( response.accepted )
				{
					let purchase = purchase_info.purchase;
					purchase.status = 'DELETED';
					return this.rest.purchase_info.delete(purchase);
				}
				return of(purchase_info);
			})
		)
		.subscribe
		({
			next:(response) =>
			{
				this.showSuccess('La compra se elimino con exito');
				this.purchase_info_list = this.purchase_info_list.filter(p => p.purchase.id != purchase_info.purchase.id);
			}
		})
	}
}
